import { authenticatedFetch } from './auth';

export interface ConsentLimits {
  max_amount_per_payment: number;
  max_payments_per_day: number;
  max_payments_per_month: number;
  currency: string;
}

export interface CreateConsentRequest {
  oauth_session_id: string;
  label?: string;
  valid_until: string; // ISO 8601
  limits: ConsentLimits;
  account_selection: string; // "all" in Phase 1
  terms_accepted: boolean;
}

export interface CreateConsentResponse {
  consent_id: string;
  status: string;
  authorization_url: string | null;
}

export interface ConsentStatusResponse {
  consent_id: string;
  status: string;
  aspsp: string;
  scope: string[];
  authorized_at: string | null;
}

/** Default consent limits */
export const DEFAULT_LIMITS: ConsentLimits = {
  max_amount_per_payment: 1000,
  max_payments_per_day: 10,
  max_payments_per_month: 100,
  currency: 'EUR',
};

/**
 * Create a PSD2 consent via Portal.
 * Requires a valid oauth_session_id from the OAuth flow redirect.
 */
export async function createConsent(
  request: CreateConsentRequest,
): Promise<CreateConsentResponse> {
  const res = await authenticatedFetch('/v1/consents', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: 'Consent creation failed' }));
    throw new Error(body.message || 'Failed to create consent');
  }
  return res.json();
}

/**
 * Poll consent status.
 */
export async function getConsentStatus(
  consentId: string,
): Promise<ConsentStatusResponse> {
  const res = await authenticatedFetch(
    `/v1/consents/${encodeURIComponent(consentId)}/status`,
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: 'Status check failed' }));
    throw new Error(body.message || 'Failed to get consent status');
  }
  return res.json();
}

// --- List Consents Types ---

export interface LimitWithUsage {
  threshold: number;
  used: number;
}

export interface ConsentLimitsWithUsage {
  max_amount_per_payment: number;
  max_payments_per_day: LimitWithUsage;
  max_payments_per_month: LimitWithUsage;
  currency: string;
}

export interface UsageSummary {
  total_payments: number;
  total_amount: number;
  last_payment_at: string | null;
}

export interface ConsentSummary {
  consent_id: string;
  aspsp_consent_id: string;
  label: string | null;
  status: string;
  scope: string[];
  accounts: string[];
  valid_from: string;
  valid_until: string;
  authorized_at: string | null;
  limits: ConsentLimitsWithUsage;
  usage_summary: UsageSummary;
}

export interface BankConsents {
  aspsp: string;
  bank_name: string;
  consents: ConsentSummary[];
}

export interface ListConsentsResponse {
  banks: BankConsents[];
}

export interface RevokeConsentResponse {
  consent_id: string;
  status: string;
}

/**
 * Revoke (delete) a consent.
 */
export async function revokeConsent(
  consentId: string,
): Promise<RevokeConsentResponse> {
  const res = await authenticatedFetch(
    `/v1/consents/${encodeURIComponent(consentId)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: 'Revocation failed' }));
    throw new Error(body.message || 'Failed to revoke consent');
  }
  return res.json();
}

/**
 * List consents for the authenticated PSU, grouped by bank.
 * When `activeOnly` is true, only non-terminal (active) consents are returned.
 */
export async function listConsents(activeOnly = false): Promise<ListConsentsResponse> {
  const params = activeOnly ? '?active_only=true' : '';
  const res = await authenticatedFetch(`/v1/consents${params}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: 'Failed to load consents' }));
    throw new Error(body.message || 'Failed to list consents');
  }
  return res.json();
}
